import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithInterceptor } from "src/utils/baseQuery";

export const OrdersApi = createApi({
  reducerPath: "OrdersApi",
  baseQuery: baseQueryWithInterceptor,
  tagTypes: ["Notification"],
  endpoints: (builder) => ({
    getOrders: builder.query<any, { page?: number; limit?: number }>({
      query: (queryParams) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order?limit=20&page=1`,
        method: "GET",
      }),
    }),
    getAllOrders: builder.mutation<any, any>({
      query: ({ limit, page }) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order?limit=${limit}&page=${page}`,
        method: "GET",
        // params: queryParams,
      }),
    }),
    getOrdersByStatus: builder.mutation<any, any>({
      query: (params) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/list`,
        method: "GET",
        params,
      }),
    }),
    searchOrders: builder.mutation<any, any>({
      query: (params) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/search`,
        method: "GET",
        params,
      }),
    }),
    getOrdersByCompany: builder.query<any, { page?: number; limit?: number }>({
      query: (queryParams) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/company/all`,
        method: "GET",
        params: queryParams,
      }),
    }),
    getOrdersById: builder.query<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}`,
        method: "GET",
      }),
    }),
    trackOrdersById: builder.query<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}/track`,
        method: "GET",
      }),
    }),
    getTrackerHistoryById: builder.query<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}/history`,
        method: "GET",
      }),
    }),
    patchOrderById: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}`,
        method: "PATCH",
        body: data,
      }),
    }),
    patchOrderReadyById: builder.mutation<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}/ready`,
        method: "PATCH",
      }),
    }),
    patchOrderCompletedById: builder.mutation<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}/completed`,
        method: "PATCH",
      }),
    }),
    getOrderEventsById: builder.query<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/${id}/events`,
        method: "GET",
      }),
    }),
    getPresets: builder.query<any, void>({
      query: () => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `location/geocode/presets`,
        method: "GET",
      }),
    }),
    unlockOrder: builder.mutation<string, { imei: string }>({
      query: ({ imei }) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/unlock/${imei}/completed`,
        method: "PATCH",
      }),
    }),
    getOrderReports: builder.mutation<string, any>({
      query: (data) => {
        let url = `report/data?from=${data.from}&to=${data.to}`;
        if (data.shipper) {
          url += `&shippingCompany=${data.shipper}`;
        }
        return {
          headers: {
            "Content-type": "application/json",
          },
          url,
          method: "GET",
        };
      },
    }),
    getOrderByCustomsCode: builder.mutation<string, any>({
      query: (data) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/filter?customsCode=${data.customsCode}`,
        method: "GET",
      }),
    }),
    getCurrentTrip: builder.mutation<any, any>({
      query: (data) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `vehicle/currentTrip?trackerid=${data.trackerid}&from=${data.from}&to=${data.to}`,
        method: "GET",
      }),
    }),

    //Escort

    createOrder: builder.mutation<any, any>({
      query: (data) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/`,
        method: "POST",
        body: data,
      }),
    }),

    updateOrder: builder.mutation<any, { id: string, eventLetterFile?: File, eventLayoutFile?: File, siraApprovalFile?: File, status?: string }>({
      query: ({ id, eventLetterFile, eventLayoutFile, siraApprovalFile, status }) => {
        const formData = new FormData();
        if (eventLetterFile) {
          formData.append("eventLetterFile", eventLetterFile);
        }
        if (eventLayoutFile) {
          formData.append("eventLayoutFile", eventLayoutFile);
        }
        if (siraApprovalFile) {
          formData.append("siraApprovalFile", siraApprovalFile);
        }
        if (status) {
          formData.append("status", status);
        }
        return {
          url: `order/files/${id}`,
          method: "PATCH",
          body: formData,
        };
      },
    }),

    getOrdersList: builder.mutation<any, any>({
      query: (params) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order`,
        method: "GET",
        params,
      }),
    }),

    getOrdersListByCompany: builder.mutation<any, any>({
      query: ({ status, ...params }) => {
        const statusPath = status ? `/${status}` : "";
        return {
          headers: {
            "Content-type": "application/json",
          },
          url: `order/company${statusPath}`,
          method: "GET",
          params, // Remaining parameters like limit and page
        };
      },
    }),

    getApprovalOrdersList: builder.mutation<any, { status: string; limit?: number; page?: number }>({
      query: ({ status, limit, page }) => ({
        headers: { "Content-type": "application/json" },
        url: `order/approval`,
        method: "GET",
        params: { status, limit, page },
      }),
    }),

    getApprovalOrderById: builder.query<any, string>({
      query: (id) => ({
        headers: {
          "Content-type": "application/json",
        },
        url: `order/approval/${id}`,
        method: "GET",
      }),
    }),

    getApprovalOrderByStatus: builder.query<any, string>({
      query: (status) => ({
        url: `order/approval`,
        method: "GET",
        headers: {
          "Content-type": "application/json",
        },
        params: {
          status,
        },
      }),
    }),
    
    getPdfFile: builder.mutation<Blob, { fileName: string }>({
      query: (payload) => ({
        url: 'user/file',           // your endpoint
        method: 'POST',
        body: payload,             // { fileName: 'xxx.pdf' }
        // Tell fetchBaseQuery how to handle the response:
        responseHandler: async (response) => {
          // If the response is not ok, throw an error
          if (!response.ok) {
            // Attempt to parse error details
            const err = await response.json().catch(() => null);
            throw err || new Error('Failed to fetch PDF');
          }
          // Return the binary data as a Blob
          return await response.blob();
        },
      }),
    }),

    approveOrder: builder.mutation<{ message: string }, string>({
      query: (orderId) => ({
        url: `order/approve/${orderId}`,
        method: 'POST',
        responseHandler: async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Unknown error');
          }
          const text = await response.text();
          return { message: text };
        },
      }),
    }),

    rejectOrder: builder.mutation<{ message: string }, { orderId: string; rejectionReason: string }>({
      query: ({ orderId, rejectionReason }) => ({
        url: `order/reject/${orderId}`,
        method: 'POST',
        body: { rejectionReason },
        responseHandler: async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Unknown error');
          }
          const text = await response.text();
          return { message: text };
        },
      }),
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetAllOrdersMutation,
  useGetOrdersByStatusMutation,
  useSearchOrdersMutation,
  useGetOrdersByCompanyQuery,
  useGetOrdersByIdQuery,
  useTrackOrdersByIdQuery,
  useGetTrackerHistoryByIdQuery,
  usePatchOrderByIdMutation,
  usePatchOrderReadyByIdMutation,
  usePatchOrderCompletedByIdMutation,
  useGetPresetsQuery,
  useGetOrderEventsByIdQuery,
  useUnlockOrderMutation,
  useGetOrderReportsMutation,
  useGetOrderByCustomsCodeMutation,
  useGetCurrentTripMutation,
  useLazyGetApprovalOrderByStatusQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useGetOrdersListMutation,
  useGetApprovalOrdersListMutation,
  useGetOrdersListByCompanyMutation,
  useGetApprovalOrderByIdQuery,
  useGetPdfFileMutation,
  useApproveOrderMutation,
  useRejectOrderMutation,
} = OrdersApi;
